import { useCallback, useState } from "react";
import { useSnackbar } from "notistack";
import { getSpatialDataset } from "../services/spatialDatasetService";
import { spatialDatasetLayerId } from "../constants/spatialDataset";
import { toLayerConfig } from "./useSpatialDatasetMapLayers";
import { useLayerActions, useLayerStore } from "../store/layer";
import type { SpatialDataset } from "../types";

type DatasetLike = Pick<SpatialDataset, "id"> & Partial<SpatialDataset>;

/**
 * Show/hide a spatial dataset overlay through the same `visibleLayers` set the
 * legend uses. Registers the COG/PMTiles layer on first show if it is not
 * already in the store.
 */
export function useSpatialDatasetLayerVisibility() {
  const { mergeSpatialDatasetLayers, setLayerVisibility } = useLayerActions();
  const { enqueueSnackbar } = useSnackbar();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const setVisible = useCallback(
    async (dataset: DatasetLike, visible: boolean) => {
      const layerId = spatialDatasetLayerId(dataset.id);
      if (!visible) {
        setLayerVisibility(layerId, false);
        return;
      }

      const existing = useLayerStore.getState().layers.find((l) => l.id === layerId);
      if (!existing) {
        setPendingId(layerId);
        try {
          const ds: SpatialDataset =
            dataset.display_url && dataset.display_type && dataset.processing_status === "completed"
              ? (dataset as SpatialDataset)
              : await getSpatialDataset(Number(dataset.id));
          if (ds.processing_status === "completed" && ds.display_url) {
            const layerConfig = toLayerConfig(ds);
            if (layerConfig) mergeSpatialDatasetLayers([layerConfig]);
          }
        } catch (error) {
          // The checkbox fires this without awaiting, so a rejection here would
          // otherwise surface as an unhandled rejection and silently leave the
          // box ticked over a map with nothing on it.
          console.error("[useSpatialDatasetLayerVisibility] failed to load dataset", dataset.id, error);
          enqueueSnackbar("Could not load that dataset for the map", { variant: "error" });
          return;
        } finally {
          setPendingId(null);
        }
      }

      // Tick the box only once a renderable layer is actually registered — a
      // dataset still processing, missing its display URL, or rejected by
      // toLayerConfig has nothing to mount, and a checked box would lie.
      const registered = useLayerStore.getState().layers.some((l) => l.id === layerId);
      if (!registered) {
        enqueueSnackbar("That dataset has no map layer to show yet", { variant: "warning" });
        return;
      }
      setLayerVisibility(layerId, true);
    },
    [mergeSpatialDatasetLayers, setLayerVisibility, enqueueSnackbar]
  );

  return { setVisible, pendingId };
}
